<script>
import { clone, isEmpty } from 'lodash'
import { PhoneType } from '@/shared/constants/contact/PhoneType'
import baseMixin from '@/mixins/baseMixin'

export default {
  name: 'BookingConfirmationsListItemEditForm',
  mixins: [baseMixin],
  props: {
    confirmation: {
      type: Object,
      default: () => null,
    },
    confirmations: {
      type: Array,
      required: true,
    },
    contacts: {
      type: Array,
      required: true,
    },
  },
  data: () => {
    return {
      isValid: false,
      payload: {
        contactId: null,
        sms: null,
        voice: null,
        email: false,
      },
      numberOfPhoneNumberProps: 3,
      rules: {
        required: () => false,
      },
    }
  },
  computed: {
    editMode() {
      return !isEmpty(this.confirmation)
    },
    contactOptions() {
      return this.contacts.filter(
        (x) => !this.confirmations.map((y) => y.contactId).includes(x.id)
      )
    },
    getConfirmation() {
      return this.contacts.find((x) => x.id === this.payload.contactId)
    },
    smsOptions() {
      const options = []

      const confirmation = this.getConfirmation
      if (!confirmation) return options

      for (let index = 1; index <= this.numberOfPhoneNumberProps; ++index) {
        if (
          !confirmation[`phone${index}`] ||
          confirmation[`phone${index}Type`] !== PhoneType.MOBILE
        )
          continue

        options.push({
          id: `Phone${index}`,
          name: confirmation[`phone${index}`],
        })
      }

      return options
    },
    voiceOptions() {
      const options = []

      const confirmation = this.contacts.find(
        (x) => x.id === this.payload.contactId
      )

      if (!confirmation) return options

      for (let index = 1; index <= this.numberOfPhoneNumberProps; ++index) {
        if (!confirmation[`phone${index}`]) continue

        options.push({
          id: `Phone${index}`,
          name: confirmation[`phone${index}`],
        })
      }

      return options
    },
    getEmail() {
      if (!this.payload || !this.payload.contactId) return ''
      return this.getConfirmation.emailAddress
    },
    enableSaveButton() {
      if (!this.payload || !this.payload.contactId) return false

      return this.payload.email || !!this.payload.sms || !!this.payload.voice
    },
  },
  created() {
    this.initValidationRules()

    if (this.editMode) {
      this.payload = clone(this.confirmation)
    } else {
      if (this.contactOptions.length === 0) return

      this.payload = {
        contactId: this.contactOptions[0].id,
        sms: null,
        voice: null,
        email: false,
      }
    }
  },
  methods: {
    initValidationRules() {
      this.rules = {
        required: (v) => !!v || this.$t('input.inputRequiredValidationText'),
      }
    },
    updatePayload(contact) {
      this.payload = {
        contactId: contact.id,
        fullName: contact.fullName,
        sms: null,
        voice: null,
        email: false,
      }
    },
    save() {
      this.$emit('save', this.payload)
      this.$emit('close')
    },
    close() {
      this.$emit('close')
    },
  },
}
</script>

<template>
  <v-card class="transparent px-6 py-4" elevation="0">
    <v-form
      ref="bookingConfirmationsListItemEditForm"
      lazy-validation
      :value="isValid"
      autocomplete="off"
    >
      <v-select
        v-if="!editMode"
        ref="booking-confirmations-list-item-edit-form__contact-selector"
        v-model="payload.contactId"
        class="booking-confirmations-list-item-edit-form__contact-selector"
        :items="contactOptions"
        item-text="fullName"
        item-value="id"
        return-object
        :label="$t('bookingCreate.confirmationsFormNameLabel')"
        :rules="[rules.required]"
        @input="updatePayload"
      ></v-select>
      <v-text-field
        v-else
        readonly
        class="booking-confirmations-list-item-edit-form__fullname-readonly"
        :label="$t('bookingCreate.confirmationsFormNameLabel')"
        :value="confirmation.fullName"
        :rules="[rules.required]"
      >
      </v-text-field>
      <v-select
        v-model="payload.sms"
        :items="smsOptions"
        item-text="name"
        item-value="id"
        :disabled="!smsOptions || smsOptions.length === 0"
        persistent-placeholder
        :placeholder="$t('general.none')"
        :no-data-text="$t('general.none')"
        clearable
        :label="$t('bookingCreate.confirmationsFormSMSLabel')"
      ></v-select>
      <v-select
        v-model="payload.voice"
        :items="voiceOptions"
        item-text="name"
        item-value="id"
        :disabled="!voiceOptions || voiceOptions.length === 0"
        persistent-placeholder
        :placeholder="$t('general.none')"
        :no-data-text="$t('general.none')"
        clearable
        :label="$t('bookingCreate.confirmationsFormVoiceLabel')"
      ></v-select>
      <v-checkbox v-if="getEmail" v-model="payload.email" color="info" dense>
        <template v-slot:label>
          <div>
            {{ $t('bookingCreate.confirmationsFormEmailCheckboxLabel') }}
            <div>
              <small>{{ getEmail }}</small>
            </div>
          </div>
        </template>
      </v-checkbox>
      <section>
        <v-tooltip bottom :disabled="enableSaveButton">
          <template v-slot:activator="{ on }">
            <div class="d-inline-block" v-on="on">
              <v-btn
                class="custom-elevation booking-confirmations-list-item-edit-form__save-btn white--text"
                color="primary"
                :disabled="!enableSaveButton"
                @click="save"
              >
                {{ $t('general.save') }}
              </v-btn>
            </div>
          </template>
          <span>
            {{
              $t(
                'bookingCreate.confirmationsFormMissingAtleastOneContactMethodValidationMessage'
              )
            }}
          </span>
        </v-tooltip>
        <v-btn
          class="booking-confirmations-list-item-edit-form__cancel-btn"
          text
          @click="close"
        >
          {{ $t('general.cancel') }}
        </v-btn>
      </section>
    </v-form>
  </v-card>
</template>
